import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/style.css';
import '../styles/mediaqueries.css';
import ThemeToggle from './ThemeToggle';

/**
 * Nav behaviour:
 *
 *  /              → SW. scrolls to top | anchors scroll within page + update URL hash
 *  /projects      → SW. goes to /     | Contact scrolls to #contact ON THIS PAGE
 *  /projects/:id  → SW. goes to /     | Contact scrolls to #contact ON THIS PAGE
 *
 * "Contact" never navigates away — it always scrolls to the Contacts
 * section that Contacts.js renders on whatever page is currently open.
 *
 * Clicking an anchor nav link scrolls to the section AND updates the URL hash,
 * e.g. clicking "About" sets the URL to /#about.
 */

const NAV = {
    landing: [
        { label: 'About',       href: '#about',      anchor: true },
        { label: 'Skills',      href: '#experience', anchor: true },
        { label: 'Projects',    href: '#projects',   anchor: true },
        { label: 'Contact',     href: '#contact',    anchor: true },
    ],
    projects: [
        { label: 'Home',        href: '/',           route: true  },
        { label: 'Projects',    href: '/projects',   route: true, active: true },
        { label: 'Contact',     href: '#contact',    anchor: true },
    ],
    detail: [
        { label: 'Home',        href: '/',           route: true  },
        { label: 'All Projects',href: '/projects',   route: true  },
        { label: 'Contact',     href: '#contact',    anchor: true },
    ],
};

export default function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const context =
        location.pathname === '/'         ? 'landing'  :
            location.pathname === '/projects' ? 'projects' : 'detail';

    const navItems = NAV[context];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => { setMenuOpen(false); }, [location.pathname]);

    const handleClick = (item, e) => {
        e.preventDefault();
        setMenuOpen(false);

        if (item.anchor) {
            // Scroll to the section
            const target = document.querySelector(item.href);
            if (target) target.scrollIntoView({ behavior: 'smooth' });

            // Update the URL hash without triggering a navigation/re-render
            window.history.replaceState(null, '', item.href);
            return;
        }

        // Route navigation
        navigate(item.href);
    };

    const handleLogo = () => {
        if (context === 'landing') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Clear the hash when going back to top
            window.history.replaceState(null, '', '/');
        } else {
            navigate('/');
        }
    };

    return (
        <>
            {/* ══ DESKTOP NAV ══ */}
            <nav
                id="desktop-nav"
                data-ctx={context}
                style={{ boxShadow: scrolled ? '0 4px 40px rgba(0,0,0,0.35)' : 'none' }}
            >
                <div className="logo" onClick={handleLogo} title={context === 'landing' ? 'Back to top' : 'Go home'}>
                    SW<span className="logo-dot">.</span>
                </div>

                {context === 'detail' && (
                    <div className="nav-breadcrumb">
                        <span className="bc-link" onClick={() => navigate('/projects')}>projects</span>
                        <span className="bc-sep">/</span>
                        <span className="bc-current">detail</span>
                    </div>
                )}

                <div className="search-container" />

                <ul className="nav-links">
                    {navItems.map(item => (
                        <li key={item.href}>
                            <a
                                href={item.href}
                                onClick={e => handleClick(item, e)}
                                className={item.active ? 'nav-active' : ''}
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <ThemeToggle />
            </nav>

            {/* ══ HAMBURGER NAV ══ */}
            <nav id="hamburger-nav" data-ctx={context}>
                <div className="logo" onClick={handleLogo} title={context === 'landing' ? 'Back to top' : 'Go home'}>
                    SW<span className="logo-dot">.</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <ThemeToggle />
                    <div className="hamburger-menu">
                        <div
                            className={`hamburger-icon${menuOpen ? ' open' : ''}`}
                            onClick={() => setMenuOpen(o => !o)}
                            aria-label="Toggle menu"
                            role="button"
                        >
                            <span /><span /><span />
                        </div>

                        <div className={`menu-links${menuOpen ? ' open' : ''}`}>
                            {navItems.map(item => (
                                <li key={item.href}>
                                    <a
                                        href={item.href}
                                        onClick={e => handleClick(item, e)}
                                        className={item.active ? 'nav-active' : ''}
                                    >
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
}